import React, { useContext, useEffect, useState, useRef } from "react";
import {
  Form,
  Input,
  Button,
  Flex,
  Checkbox,
  Row,
  Typography,
  Col,
} from "antd";
import { useNavigate } from "react-router-dom";
import {
  CloseOutlined,
  LockOutlined,
  UserOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { TResponseModel, TSignUp, TUser } from "../../types/config";
import EncryptUtils from "../../utils/encrypt";
import useFetch from "../../hooks/useFetch";
import { eLOGINMODE, eResultCode, eUserType } from "../../utils/enum";
import useToast from "../../hooks/useToast";
import AuthUtil from "../../utils/auth";
import { ToastOpen, ToastType } from "../../state/toast/slice";
import Utils from "../../utils";
import style from "./auth.module.css";
import { ApiSignin, OTPSumbission } from "../../utils/api.constant";
import Text, { LanguageContext } from "../../context/language-context";
import { LoaderContext } from "../../context/loader";
import { allowOnlyNumericInput_onKeyDown } from "../../utils/input.validator";

interface LoginProps {
  autoEmailorPhone: string;
  autoPassword: string;
  // setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  // isLoading: boolean;
}

const RESEND_OTP_SECONDS = 60;

const Login: React.FC<LoginProps> = ({ autoEmailorPhone, autoPassword }) => {
  const navigate = useNavigate();
  const { post } = useFetch();
  const [form] = Form.useForm();
  const [otpForm] = Form.useForm();
  const { showToast, onShowToast } = useToast();
  const [loginMode, setLoginMode] = useState<number>(eLOGINMODE.PASSWORD);
  const [otpSend, setOtpSend] = useState<boolean>(false);
  const { userLanguage, userLanguageChange } = useContext(LanguageContext);

  const contextForLoader = useContext(LoaderContext);
  // const { isLoading, setIsLoading } = contextForLoader as any;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [resendTimer, setResendTimer] = useState<number>(0);
  const resendIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const somethingWentWrongValidation = Text({
    tid: "SOMETHING_WENT_WRONG",
    def: "Something went wrong",
  });

  // useEffect(() => {
  //   if (localStorage && localStorage.getItem("emailOrMobile") && localStorage.getItem("password")) {
  //     form.setFieldsValue({
  //       emailOrMobile: EncryptUtils.decrypt(
  //         localStorage.getItem("emailOrMobile") as string
  //       ),
  //     });
  //     form.setFieldsValue({
  //       password: EncryptUtils.decrypt(
  //         localStorage.getItem("password") as string
  //       ),
  //     });
  //   }
  // }, []);

  const onFinish = async (values: TSignUp) => {
    try {
      setIsLoading(true);

      // Prepare payload for API request
      const payload: { data: TSignUp } = {
        data: {
          userName: values?.emailOrMobile
            ? values.emailOrMobile
            : otpForm.getFieldValue("emailOrMobile") || "",
          // userName : "norikedarsharma@gmail.com",
          password: values.password
            ? EncryptUtils.encrypt(values.password as string)
            : "",
          loginMode: loginMode,
        },
      };

      if (values?.otp?.length) {
        payload.data.otp = values.otp;
      }

      // Make API request for sign-in
      const response: TResponseModel = await post(ApiSignin, payload);
      // Handle API response
      const { dataResponse, data } = response;
      const { returnCode, description } = dataResponse;

      const authToken = data?.token;
      if (returnCode === eResultCode.SUCCESS) {
        if (authToken) {
          AuthUtil.setToken(authToken as string);
          AuthUtil.activeLoggedUserService();
        }

        if (eLOGINMODE.OTP === loginMode && !otpSend) {
          setOtpSend(true);
        }

        if (values?.remember) {
          localStorage.setItem(
            "emailOrMobile",
            EncryptUtils.encrypt(values.emailOrMobile as string)
          );
          localStorage.setItem(
            "password",
            EncryptUtils.encrypt(values.password as string)
          );
        }

        if (loginMode === eLOGINMODE.PASSWORD || values?.otp?.length) {
          if (parseInt(data?.userType) === eUserType.ADMIN) {
            Utils.redirectUrl("/admin/dashboard");
          } else if (parseInt(data?.userType) === eUserType.USER) {
            Utils.redirectUrl("/dashboard");
          }
        }

        setIsLoading(false);
      } else {
        // Display error toast
        setIsLoading(false);
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.leftBottom,
        content: somethingWentWrongValidation,
      });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (autoEmailorPhone && autoPassword) {
      form.setFieldValue("emailOrMobile", autoEmailorPhone);
      form.setFieldValue("password", autoPassword);
    }
  }, [autoEmailorPhone, autoPassword]);

  const validateEmailOrMobile = (_: any, value: string) => {
    if (!value) {
      // return Promise.reject(Text({ tid: "email_mobile_error", def: "Please input your Email or Mobile!" }));
      return Promise.reject(
        userLanguage === "sk"
          ? "कृपया उचितां दूरवाणीसङ्ख्यां / उचितं अन्तर्जालीयं पत्रसङ्केतं प्रविश्यताम्!"
          : userLanguage === "hi"
            ? "कृपया अपना ईमेल या मोबाइल नम्बर दर्ज करें!"
            : "Please input a valid Mobile Number or Email!"
      );
    }
    const isNumeric = /^\d+$/.test(value);
    if (isNumeric) {
      if (value.length !== 10) {
        return Promise.reject(
          userLanguage === "sk"
            ? "कृपया वैधं निष्तन्त्री सङ्ख्या निवेशयन्तु!"
            : userLanguage === "hi"
              ? "कृपया एक वैध मोबाइल नम्बर दर्ज करें!"
              : "Please input a valid Mobile Number!"
        );
      }
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return Promise.reject(
          userLanguage === "sk"
            ? "कृपया वैधं ईपत्र पत्रसङ्केत निवेशयन्तु!"
            : userLanguage === "hi"
              ? "कृपया एक वैध ईमेल दर्ज करें!"
              : "Please input a valid Email!"
        );
      }
    }
    return Promise.resolve();
  };

  // Start or reset the resend timer
  const startResendTimer = () => {
    setResendTimer(RESEND_OTP_SECONDS);
    if (resendIntervalRef.current) clearInterval(resendIntervalRef.current);
    resendIntervalRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          if (resendIntervalRef.current)
            clearInterval(resendIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // When OTP is sent, start the timer
  useEffect(() => {
    if (otpSend) {
      startResendTimer();
      onShowToast({
        type: ToastType.success,
        title: <CheckOutlined />,
        position: ToastOpen.leftBottom,
        content: "OTP sent successfully!",
      });
    } else {
      setResendTimer(0);
      if (resendIntervalRef.current) clearInterval(resendIntervalRef.current);
    }
    // Cleanup on unmount
    return () => {
      if (resendIntervalRef.current) clearInterval(resendIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpSend]);

  
  useEffect(() => {
    const lang = localStorage.getItem("rcml-lang");
    if (lang !== "hi") {
      localStorage.setItem("rcml-lang", "hi");
      window.location.reload();
    }
  }, []);

  return (
    <div className={style.parentWrapper}>
      <div>
        <Typography.Title level={3}>
          {Text({ tid: "login", def: "Login" })}
        </Typography.Title>
      </div>
      <div className={style.methodButtons}>
        <button
          className={
            loginMode === eLOGINMODE.PASSWORD
              ? `${style.activeButton} ${style.passwordButton}`
              : style.passwordButton
          }
          onClick={(e) => {
            e.preventDefault();
            setLoginMode(eLOGINMODE.PASSWORD);
          }}
        >
          {Text({ tid: "via_password", def: "Via password" })}
        </button>
        <button
          className={
            loginMode === eLOGINMODE.OTP
              ? `${style.activeButton} ${style.otpButton}`
              : style.otpButton
          }
          onClick={(e) => {
            e.preventDefault();
            setLoginMode(eLOGINMODE.OTP);
          }}
        >
          {Text({ tid: "via_otp", def: "Via OTP" })}
        </button>
      </div>
      {loginMode === eLOGINMODE.PASSWORD ? (
        <Form
          name='login'
          initialValues={{
            remember: true,
            emailOrMobile:
              autoEmailorPhone ||
              (typeof localStorage !== "undefined" &&
                localStorage.getItem("emailOrMobile")
                ? EncryptUtils.decrypt(
                  localStorage.getItem("emailOrMobile") as string
                )
                : ""),
            password:
              autoPassword ||
              (typeof localStorage !== "undefined" &&
                localStorage.getItem("password")
                ? EncryptUtils.decrypt(
                  localStorage.getItem("password") as string
                )
                : ""),
          }}
          onFinish={onFinish}
          size='middle'
          form={form}
          layout='vertical'
        >
          <Form.Item
            name='emailOrMobile'
            label={Text({
              tid: "email_address_mobile_number",
              def: "Email address / mobile number",
            })}
            rules={[
              {
                required: true,
                message: "",
              },
              { validator: validateEmailOrMobile },
            ]}
          >
            <Input
              placeholder={Text({
                tid: "email_address_mobile_number",
                def: "Email Id / Mobile",
              })}
            />
          </Form.Item>

          <Form.Item
            name='password'
            label={Text({ tid: "password", def: "Password" })}
            rules={[
              {
                required: true,
                message: Text({
                  tid: "password_error",
                  def: "Please input your Password!",
                }),
              },
            ]}
          >
            <Input.Password
              placeholder={Text({ tid: "password", def: "Password" })}
            />
          </Form.Item>
          <Form.Item
            id='rememberMe'
            style={{ marginTop: "-5px", minHeight: "100%" }}
          >
            <Form.Item
              name='remember'
              valuePropName='checked'
              noStyle
            >
              <Checkbox>
                {Text({ tid: "remember_me", def: "Remember me" })}
              </Checkbox>
            </Form.Item>
          </Form.Item>
          <Form.Item>
            <Button
              block
              type='primary'
              htmlType='submit'
              disabled={isLoading}
            >
              {Text({ tid: "submit", def: "Submit" })}
            </Button>
          </Form.Item>
        </Form>
      ) : (
        <>
          {!otpSend ? (
            <Form
              name='login'
              initialValues={{ remember: true }}
              form={otpForm}
              onFinish={onFinish}
              size='middle'
              layout='vertical'
            >
              <Form.Item
                name='emailOrMobile'
                label={Text({
                  tid: "email_address_mobile_number",
                  def: "Email address / mobile number",
                })}
                rules={[
                  {
                    required: true,
                    message: "",
                  },
                  { validator: validateEmailOrMobile },
                ]}
              >
                <Input
                  placeholder={Text({
                    tid: "email_address_mobile_number",
                    def: "Email Id / Mobile",
                  })}
                />
              </Form.Item>
              <Form.Item>
                <Button
                  block
                  type='primary'
                  htmlType='submit'
                  disabled={isLoading}
                >
                  {Text({ tid: "SEND_OTP", def: "Send OTP" })}
                </Button>
              </Form.Item>
            </Form>
          ) : (
            <Form
              name='otp'
              initialValues={{ remember: true }}
              onFinish={onFinish}
              size='middle'
              layout='vertical'
            >
              <Form.Item
                name='otp'
                label={Text({ tid: "otp", def: "Enter OTP" })}
                rules={[
                  {
                    required: true,
                    message: Text({
                      tid: "via_otp",
                      def: "OTP is required",
                    }),
                  },
                  {
                    pattern: /^\d{6}$/,
                    message: Text({
                      tid: "invalid_otp",
                      def: "OTP must be 6 digits",
                    }),
                  },
                ]}
              >
                <Input
                  placeholder={Text({ tid: "otp", def: "Enter OTP" })}
                  maxLength={6}
                  onKeyDown={allowOnlyNumericInput_onKeyDown}
                />
              </Form.Item>
              <Form.Item>
                <Button
                  block
                  type='primary'
                  htmlType='submit'
                  disabled={isLoading}
                  loading={isLoading}
                >
                  {Text({ tid: "submit", def: "Submit" })}
                </Button>
              </Form.Item>
              <div style={{ textAlign: "right", marginTop: "-10px" }}>
                {resendTimer > 0 ? (
                  <span style={{ fontSize: 12, color: "#888" }}>
                    {Text({ tid: "RESEND_OTP", def: "Resend OTP" })} (
                    {resendTimer}s)
                  </span>
                ) : (
                  <a
                    style={{ fontSize: 12, cursor: "pointer" }}
                    onClick={() => {
                      const emailOrMobile =
                        otpForm.getFieldValue("emailOrMobile");
                      if (emailOrMobile) {
                        onFinish({
                          emailOrMobile,
                          password: "",
                          loginMode: eLOGINMODE.OTP,
                        });
                        startResendTimer();
                      }
                    }}
                  >
                    {Text({ tid: "RESEND_OTP", def: "Resend OTP" })}
                  </a>
                )}
              </div>
            </Form>
          )}
        </>
      )}
    </div>
  );
};

export default Login;
